import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "../lib/api";
import { DEFAULT_DIRECTORS_PAGE } from "../lib/homeContent";

const BIOSKETCH_URL =
  "https://customer-assets.emergentagent.com/job_energy-capital/artifacts/t8tlq3l6_biosketch.png";
// Horizontal focus (%) for each of the 6 figures, left→right (background-position-x with 600% size)
const FIGURE_POS_X = [1.4, 20.6, 40.4, 60.2, 80, 99.8];

const BiosketchCrop = ({ index }) => {
  const posX = FIGURE_POS_X[index % FIGURE_POS_X.length];
  return (
    <div
      className="h-full w-full bg-sp-dark"
      style={{
        backgroundImage: `url("${BIOSKETCH_URL}")`,
        backgroundSize: "600% auto",
        backgroundPosition: `${posX}% 22%`,
        backgroundRepeat: "no-repeat",
      }}
    />
  );
};

export default function Directors() {
  const [directors, setDirectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [c, setC] = useState(DEFAULT_DIRECTORS_PAGE);

  useEffect(() => {
    api
      .get("/directors")
      .then(({ data }) => setDirectors(data))
      .catch(() => setDirectors([]))
      .finally(() => setLoading(false));
    api.get("/content/directors").then(({ data }) => setC({ ...DEFAULT_DIRECTORS_PAGE, ...data })).catch(() => {});
  }, []);

  return (
    <div data-testid="directors-page">
      <section className="pt-40 pb-16 bg-sp-bg">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <p className="sp-eyebrow">{c.eyebrow}</p>
          <h1 className="mt-6 font-serif text-4xl md:text-6xl text-sp-deep max-w-3xl leading-[1.08]">
            {c.title}
          </h1>
          <p className="mt-6 font-sans text-lg text-sp-soft max-w-2xl">
            {c.subtitle}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-32">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-96 bg-white border border-sp-navy/10 animate-pulse rounded-sm" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {directors.map((d, i) => (
              <motion.article
                key={d.id}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.1 }}
                data-testid={`director-card-${d.id}`}
                className="group overflow-hidden rounded-sm border border-sp-navy/10 bg-white hover:shadow-[0_12px_48px_rgba(40,78,120,0.12)] transition-shadow duration-500"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  {d.photo_url ? (
                    <img src={d.photo_url} alt={d.name} className="h-full w-full object-cover" />
                  ) : (
                    <BiosketchCrop index={i} />
                  )}
                </div>
                <div className="p-7">
                  {d.expertise && <p className="sp-eyebrow">{d.expertise}</p>}
                  <h3 className="mt-3 font-serif text-2xl text-sp-deep">{d.name}</h3>
                  <p className="mt-1 font-sans text-sm font-medium text-sp-navy">{d.title}</p>
                  <p className="mt-4 font-sans text-sm leading-relaxed text-sp-soft">{d.bio}</p>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
